// Node modules
import * as Sentry from '@sentry/react';

// Exceptions
import { InternalServerErrorException } from '../exceptions/InternalServerErrorException';
import { UnauthorizedException } from '../exceptions/UnauthorizedException';
import { RateLimitedException } from '../exceptions/RateLimitedException';
import { ResourceNotFoundException } from '../exceptions/ResourceNotFoundException';
import { FailedToFetchException } from '../exceptions/FailedToFetchException';
import { InvalidInputException } from '../exceptions/InvalidInputException';

const clientInstanceID = String(Math.round(Math.random() * 1_000_000_000));

export type EventData = ComposerEvent | MessageAndSoundEvent | IFrameEvent | GroupEvent;

export interface OverlayEvent<T = EventData> {
	EventID?: string;
	UnixMS: number;
	Attempts?: number;
	Data: T;
	InteractionSettings: any;
	OverlaySettings: {
		Popup: PopupSettings;
		Spot: PositionSettings;
		ChaosGoal: PositionSettings;
		ExtensionHint: ExtensionHintSettings;

		// Percentage as an int from [0, 100]
		// default volume
		Volume: number;
	};
}

export interface ComposerEvent {
	type: 'overlay_composer';
	overlay_spot: IFrameEvent;
	overlay_popup: MessageAndSoundEvent | IFrameEvent;
	// never used ???
	overlay_fs: IFrameEvent;
}

export interface MessageAndSoundEvent {
	type: 'popup';
	Message: string;
	Sound?: string;
}

export interface IFrameEvent {
	type: 'overlay_fs' | 'overlay_spot' | 'overlay_popup';
	URL: string;
	Message?: string;
	MaxDuration?: number;
	OverlayParams?: any;
	Placement?: {
		X: number;
		Y: number;
		Width: number;
		Height: number;
		Rotation: number;
		Mirrored: boolean;
	};
	Kind?: 'custom' | '';
}

export interface GroupEvent {
	type: 'group';
	Events: Array<{
		NonBlocking: boolean;
		Event: MessageAndSoundEvent | IFrameEvent;
	}>;
}

export function getEventKinds(event: EventData): Array<'custom' | '' | undefined> {
	if (event.type === 'overlay_composer' || event.type === 'popup' || !event.type) {
		return [];
	}
	if (event.type === 'group') {
		return event.Events.map((e) => getEventKinds(e.Event)).flat();
	}
	return [event.Kind];
}

export async function getPollEvent(obsKey: string, type?: string): Promise<{ Events?: OverlayEvent[] }> {
	const res = await doRequest(
		`${import.meta.env.VITE_BACKEND_API}/ack-hook/poll?key=${obsKey}&instance=${clientInstanceID}${type ? `&type=${type}` : ''}&cacheBust=${Math.random()}`,
	);
	if (!res) {
		return {};
	}
	return res;
}

export async function postStart(obsKey: string, type?: string) {
	const url = new URL(`${import.meta.env.VITE_BACKEND_API}/ack-hook/started`);
	url.searchParams.set('key', obsKey);
	url.searchParams.set('instance', clientInstanceID);
	if (type) {
		url.searchParams.set('type', type);
	}
	// pass user timezone
	try {
		url.searchParams.set('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);
	} catch (e) {
		console.error('Failed to get timezone - not passing', e);
	}
	return doRequest(url, 'POST');
}

export async function postEventAckNackSkip(type: 'ack' | 'nack' | 'skip', eventId: string, reason?: string) {
	const url = new URL(`${import.meta.env.VITE_BACKEND_API}/v2/actions/${type}/${eventId}`);
	url.searchParams.set('instance', clientInstanceID);
	if (reason) {
		if (reason.length > 150) {
			console.warn('Reason too long, trimming to 150 characters', reason);
			reason = reason.slice(0, 150);
		}
		url.searchParams.set('reason', reason);
	}
	return doRequest(url, 'POST');
}

export async function pingBackend(obsKey: string, type?: string, integrations?: string[], currentEvent?: string) {
	integrations ??= [];
	return doRequest(
		`${import.meta.env.VITE_BACKEND_API}/ack-hook/ping?key=${obsKey}&instance=${clientInstanceID}${type ? `&type=${type}` : ''}${
			integrations.length ? `&integrations=${encodeURIComponent(integrations.join(','))}` : ''
		}&currentEvent=${currentEvent}`,
		'POST',
	);
}

export interface OverlaySettings {
	Popup: PopupSettings;
	Spot: PositionSettings;
	ChaosGoal: PositionSettings;
	ExtensionHint: ExtensionHintSettings;
	InteractionsPausedUntil: string; // ISO 8601 date string
	ShouldShowPauseIndicator?: boolean;
	// in % -> divide by 100 before using as <Audio volume={v}>
	Volume: number;
	Dungeon?: {
		Enabled: boolean;
		Position: PositionSettings;
	};
}

export interface PopupSettings extends PositionSettings {
	FontColor: ColorSettings;
	BackgroundColor: ColorSettings;
	IconColor: ColorSettings;
	IconBackgroundColor: ColorSettings;
	BorderColor: ColorSettings;
	Size: string;
	Enabled: boolean;
	TimeSeconds: number;
	Sound: string;
	// in % -> divide by 100 before using as <Audio volume={v}>
	Volume: number;
}

export interface ColorSettings {
	Red: number;
	Green: number;
	Blue: number;
	Alpha: number;
}

export interface PositionSettings {
	X: number;
	Y: number;
	Height: number;
	Width: number;
}

export interface ExtensionHintSettings {
	Enabled: boolean;
	SecondsOn: number;
	SecondsOff: number;
}

export async function getSettings(obsKey: string): Promise<OverlaySettings> {
	return doRequest(`${import.meta.env.VITE_BACKEND_API}/ack-hook/settings?key=${obsKey}&instance=${clientInstanceID}`);
}

export async function getUserInfo(obsKey: string): Promise<{ UserID: string }> {
	return doRequest(`${import.meta.env.VITE_BACKEND_API}/ack-hook/me?key=${obsKey}`);
}

async function doRequest(url: string | URL, method: 'GET' | 'POST' = 'GET'): Promise<any> {
	let response, responseText;
	try {
		response = await fetch(url, { method });
		responseText = await response.text();
	} catch (error) {
		if (error instanceof TypeError && error.message === 'Failed to fetch') {
			throw new FailedToFetchException('Failed to load');
		} else {
			Sentry.captureException(error);
			throw error;
		}
	}

	if (response.status < 300) {
		if (/application\/json/.test(response.headers.get('Content-Type') || '')) {
			return JSON.parse(responseText);
		}
		return responseText;
	} else if (response.status === 400) {
		throw new InvalidInputException(responseText);
	} else if (response.status === 401) {
		throw new UnauthorizedException(responseText);
	} else if (response.status === 404) {
		throw new ResourceNotFoundException(responseText);
	} else if (response.status === 429) {
		throw new RateLimitedException(responseText);
	} else if (response.status === 500) {
		throw new InternalServerErrorException(responseText);
	} else if (response.status === 502 || response.status === 503) {
		throw new FailedToFetchException('Failed to load: ' + response.status);
	}
	Sentry.captureEvent({
		message: `Unknown error occurred ${responseText}`,
	});
	throw new Error(`Unknown error occurred ${responseText}`);
}
